import React from 'react';
import { VStack } from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import { useAuthenticatedUserInfo } from '../../services/Contexts/AuthenticatedUserInfoContext';
import { useAllContacts } from '../../hooks/useContact';
import { useEmployees } from '../../hooks/useEmployee';
import { useClients } from '../../hooks/useClient';
import { useCompanyProjects } from '../../hooks/useProject';
import { useTemplates } from '../../hooks/useTemplates';
import { useAiWordpressMonthlyUsage } from '../../hooks/useWordpress';
import PlanUsageBar from '../Reusables/PlanUsageBar/PlanUsageBar';
import FEATURE_BARS from './featureBarsConfig';
import { FEATURE_KEYS } from '../../utils/featureUtils';

/**
 * GlobalUsageLimitsBar
 *
 * Renders plan usage bars for tracked features, scoped to relevant URL paths.
 * Each bar only appears on its matching pages AND only when at/near the limit
 * (hideWhenHealthy). Zero visual impact under normal conditions.
 *
 * To add a new feature: add an entry to FEATURE_BARS with matchPaths,
 * then supply the appropriate currentUsage below.
 */

const GlobalUsageLimitsBar = () => {
	const { pathname } = useLocation();
	const { companyId } = useAuthenticatedUserInfo();

	// Always call hooks unconditionally (Rules of Hooks).
	// Visibility is controlled in JSX, not by skipping hook calls.
	const { data: contactCountData } = useAllContacts(companyId, { limit: 1 });
	const currentContactCount =
		contactCountData?.data?.pagination?.totalContacts ?? 0;

	const { data: employees } = useEmployees();
	const currentEmployeeCount = employees?.length ?? 0;

	const { data: clientsData } = useClients(companyId);
	const currentClientCount = Array.isArray(clientsData)
		? clientsData.length
		: (clientsData?.clients?.length ?? 0);

	const { data: projectsData } = useCompanyProjects(companyId);
	const currentProjectCount = Array.isArray(projectsData)
		? projectsData.length
		: 0;

	const { data: templates } = useTemplates();
	const currentBuilderTemplateCount = Array.isArray(templates)
		? templates.filter((t) => t.design).length
		: 0;

	const { data: aiGenerationUsage } = useAiWordpressMonthlyUsage(companyId);
	const currentAiGenerationCount = aiGenerationUsage?.consumed ?? 0;

	const usageByKey = {
		[FEATURE_KEYS.CONTACTS]: currentContactCount,
		[FEATURE_KEYS.EMPLOYEES]: currentEmployeeCount,
		[FEATURE_KEYS.CLIENTS]: currentClientCount,
		[FEATURE_KEYS.PROJECTS]: currentProjectCount,
		[FEATURE_KEYS.EMAIL_BUILDER]: currentBuilderTemplateCount,
		[FEATURE_KEYS.AI_WORDPRESS_CONTENT]: currentAiGenerationCount,
	};

	const visibleBars = FEATURE_BARS.filter((bar) =>
		bar.matchPaths.some((p) =>
			bar.exactMatch ? pathname === p : pathname.startsWith(p),
		),
	);

	if (visibleBars.length === 0) return null;

	return (
		<VStack spacing={2} w='100%' mb={2}>
			{visibleBars.map((bar) => (
				<PlanUsageBar
					key={bar.featureKey}
					featureKey={bar.featureKey}
					currentUsage={usageByKey[bar.featureKey] ?? 0}
					label={bar.label}
					unitLabel={bar.unitLabel}
					upgradeTitle={bar.upgradeTitle}
					upgradeDescription={bar.upgradeDescription}
					hideWhenHealthy={bar.hideWhenHealthy ?? true}
					healthyThreshold={bar.healthyThreshold ?? 0.75}
					showUpgradeButton={bar.showUpgradeButton ?? true}
					mb={0}
				/>
			))}
		</VStack>
	);
};

export default GlobalUsageLimitsBar;
